import { LineItemWithProdType } from "@/app/types/line-items";
import { getLocalisedNumber, useTranslateText } from "@/app/utils/translations";
import React from "react";
import { View } from "react-native";
import { TouchableOpacity } from "../../Buttons";
import { Text } from "../../Text";
import { getLineItemStyles } from "./styles";
import { formatMoney } from "@/app/utils/currency";
import Options from "./Options";
import ComboChoices from "./ComboChoices";
import { useSelector } from "react-redux";
import {
  getComboChoicesForBasketLine,
  getOptionPriceForBasketLine,
  getSelectedChoicesForBasketLine,
} from "@/app/selectors/options";
import { RootState } from "@/app/reducers";

type LineItemProps = {
  item: LineItemWithProdType;
  onItemPress: (id: string) => void;
};

const LineItem = ({ item, onItemPress }: LineItemProps) => {
  const selectedLineOptions = useSelector((state: RootState) =>
    getSelectedChoicesForBasketLine(state, { lineItemId: `${item.id}` }),
  );

  const optionPriceForBasketLine = useSelector((state: RootState) =>
    getOptionPriceForBasketLine(state, {
      lineItemId: `${item.id}`,
      onClose: () => {},
      onUpdatePress: () => {},
    }),
  );

  const comboChoices = useSelector((state: RootState) =>
    getComboChoicesForBasketLine(state, Number(item.id)),
  );

  const { id, title, quantity, total, _isUnavailable } = item;
  const totalPrice = total + optionPriceForBasketLine;
  const { t, locale } = useTranslateText();

  const isBasketItemSelected = false;

  const styles = getLineItemStyles(isBasketItemSelected);

  return (
    <TouchableOpacity
      style={styles.lineContainer}
      onPress={() => onItemPress(`${id}`)}
      accessible={true}
      accessibilityRole="button"
      accessibilityLabel={`${title} - Quantity: ${quantity}`}
      testID={`basket-item-${id}`}
    >
      <View style={styles.lineItemContainer}>
        <View style={styles.lineQuantityContainer}>
          <View style={styles.lineCircle}>
            <Text
              style={styles.lineQuantityText}
              accessible
              accessibilityRole="text"
              accessibilityLabel="Line item quantity"
              testID={`line-quantity-text-${id}`}
            >
              {getLocalisedNumber(quantity, locale)}
            </Text>
          </View>
        </View>
        <View style={styles.lineItemTitleContainer}>
          <Text
            accessible
            accessibilityRole="text"
            accessibilityLabel="Line item product name"
            style={styles.lineItemTitleText}
          >
            {title}
          </Text>
        </View>
        <View style={styles.lineItemTotalContainer}>
          <Text
            accessible
            accessibilityRole="text"
            accessibilityLabel="Line item total price"
            style={styles.lineItemTotalText}
          >
            {_isUnavailable
              ? t("checkoutScreen.unavailable")
              : formatMoney(totalPrice, true, locale).amount}
          </Text>
        </View>
      </View>
      {selectedLineOptions &&
        selectedLineOptions.length > 0 &&
        !_isUnavailable && (
          <Options
            isBasketItemSelected={isBasketItemSelected}
            lineOptions={selectedLineOptions}
          />
        )}
      {comboChoices.length > 0 && !_isUnavailable && (
        <ComboChoices
          isBasketItemSelected={isBasketItemSelected}
          choices={comboChoices}
        />
      )}
    </TouchableOpacity>
  );
};

export default LineItem;
